/* Native-scroll product story. Scroll controls the camera until the visitor rotates it. */
(() => {
  if (window.MoviwiHomeModel) return;
  const controllers = new WeakMap(), reduced = matchMedia('(prefers-reduced-motion: reduce)'), mobile = matchMedia('(max-width: 760px)');
  let library;
  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => {const t = clamp(value); return t*t*(3-2*t);};
  // A CORS-cross-origin classic script has an opaque import() base URL in the
  // Shopify editor. Resolve protocol-relative asset_url output against the page.
  const assetSource = source => new URL(source, document.baseURI).href;
  const environmentSource = source => {
    if (!source || source === 'neutral' || source === 'legacy') return source || 'neutral';
    const url = new URL(source, document.baseURI);
    // model-viewer 4.4 selects RGBE by the URL suffix, including query/hash.
    // Preserve Shopify's cache version; a fragment supplies the format hint
    // without changing the HTTP request or the cached asset being fetched.
    if (/\.hdr$/i.test(url.pathname) && !/\.hdr$/i.test(url.href)) url.hash = 'environment.hdr';
    return url.href;
  };
  const ensureLibrary = (url, decoder) => {
    if (!library) library = (async () => {
      if (!customElements.get('model-viewer')) await import(assetSource(url));
      await customElements.whenDefined('model-viewer');
      const Viewer = customElements.get('model-viewer'); Viewer.meshoptDecoderLocation = assetSource(decoder);
      return Viewer;
    })().catch(error => {library = null; throw error;});
    return library;
  };
  function mount(root) {
    if (controllers.has(root)) return;
    const story = root.querySelector('[data-product-narrative]'), stage = root.querySelector('[data-model-stage]');
    if (!story || !stage) return;
    const abort = new AbortController(), {signal} = abort;
    const on = (el, event, fn, options = {}) => el?.addEventListener(event, fn, {...options, signal});
    const find = selector => stage.querySelector(selector);
    const poster = find('[data-model-poster]'), target = find('[data-model-mount]'), status = find('[data-model-status]');
    const surface = stage.closest('[data-home-section]'), pin = story.querySelector('[data-product-pin]');
    const features = [...story.querySelectorAll('[data-product-step]')], markers = [...story.querySelectorAll('[data-story-marker]')], scenes = [...story.querySelectorAll('[data-story-scene]')];
    let viewer, loading = false, visible = false, frame = 0, timeout = 0, retry = 0, attempts = 0, progress = 0, pass = 0, interacting = false, lastCamera = '', current = -1;
    const yaw = Number(stage.dataset.modelYaw) || 0, elevation = Number(stage.dataset.modelElevation) || 65;
    const motion = () => !reduced.matches && surface?.dataset.motion !== 'false';
    const guided = () => motion() && stage.dataset.modelScroll !== 'false';
    const clearStatus = () => {status.textContent = ''; stage.removeAttribute('aria-busy');};
    const setCamera = (angle, polar) => {
      if (!viewer || interacting) return;
      const value = `${angle.toFixed(2)}deg ${polar.toFixed(2)}deg auto`;
      if (lastCamera !== value) {viewer.setAttribute('camera-orbit', value); lastCamera = value;}
    };
    const select = index => {
      if (index === current) return;
      current = index; story.dataset.storyStep = String(index);
      features.forEach((el, i) => el.classList.toggle('is-current', i === index));
      markers.forEach((el, i) => {el.classList.toggle('is-current', i === index); el.classList.toggle('is-complete', i < index);});
      scenes.forEach((el, i) => el.classList.toggle('is-current', i === index));
    };
    function draw() {
      frame = 0; if (signal.aborted) return;
      if (!motion()) {
        story.classList.remove('product-story-ready');
        stage.style.removeProperty('--story-model-scale'); stage.style.removeProperty('--story-model-lift');
        story.style.removeProperty('--beat-progress');story.style.removeProperty('--story-exit');
        if (!interacting) setCamera(yaw, elevation);
        return;
      }
      if (!visible) return;
      const box = story.getBoundingClientRect(), height = pin.getBoundingClientRect().height;
      const p = clamp(-box.top / Math.max(1, box.height - height));
      select(Math.min(features.length - 1, Math.floor(clamp((p-.08)/.84) * features.length)));
      story.style.setProperty('--story-progress', String(p));
      story.style.setProperty('--story-exit',String(ease((p-.87)/.13)));
      story.style.setProperty('--beat-progress', String(ease(clamp((p-.08)/.84)*features.length-current)));
      // Each selling point holds an angle. Only the joins interpolate.
      const arrival = ease(p/.15), second = ease((p-.31)/.13), third = ease((p-.63)/.13), leave = ease((p-.93)/.07);
      stage.style.setProperty('--story-model-scale', String(.96 + arrival*.04 - third*.08 - leave*.02));
      stage.style.setProperty('--story-model-lift', `${second*(mobile.matches?-2:-4) + third*(mobile.matches?0:-3)}px`);
      if (guided()) setCamera(yaw*(1-arrival) - second*30 + third*48, elevation + (76-elevation)*arrival - second*12 + third*5);
    }
    const schedule = () => {if (!frame && !document.hidden) frame = requestAnimationFrame(draw);};
    const syncMotion = () => {
      story.classList.toggle('product-story-ready', motion() && features.length > 0);
      schedule();
    };
    const showImage = () => {
      pass++; loading = false; clearTimeout(timeout); viewer?.remove(); viewer = null;
      target.hidden = true; target.replaceChildren(); poster.hidden = false;
      stage.classList.remove('model-loaded');
      clearStatus(); lastCamera = '';
    };
    const fail = (id, reason = `${stage.dataset.modelState || 'model'}-timeout`) => {
      if (signal.aborted || id !== pass) return;
      showImage(); stage.dataset.modelState = 'error'; stage.dataset.modelError = reason; status.textContent = status.dataset.errorLabel;
      if (attempts < 2 && reason !== 'webglcontextlost') retry = setTimeout(() => {retry = 0;if (visible && !signal.aborted) load();}, 1200);
    };
    const armTimeout = id => {clearTimeout(timeout);timeout = setTimeout(() => fail(id), 45000);};
    async function load() {
      if (loading || viewer || attempts >= 2 || !stage.dataset.modelUrl) return;
      clearTimeout(retry); retry = 0; attempts++; progress = 0;
      const id = ++pass; loading = true; stage.dataset.modelState = 'library'; stage.removeAttribute('data-model-error'); stage.setAttribute('aria-busy', 'true'); status.textContent = status.dataset.loadingLabel;
      armTimeout(id);
      try {
        await ensureLibrary(stage.dataset.modelLibrary, stage.dataset.modelDecoder);
        if (signal.aborted || id !== pass) return;
        stage.dataset.modelState = 'model';
        armTimeout(id);
        viewer = document.createElement('model-viewer');
        // The outer section observer already defers 3D. A second viewport wait here
        // leaves an offscreen model idle while our failure timer is running.
        const attrs = {alt:'Moviwi silver motion gaming console, interactive 3D view.', loading:'eager', 'camera-controls':'', 'disable-zoom':'', 'disable-pan':'', 'interaction-prompt':'none', 'touch-action':'pan-y', 'camera-orbit':`${yaw}deg ${elevation}deg auto`, 'field-of-view':'30deg', 'min-camera-orbit':'auto 35deg auto', 'max-camera-orbit':'auto 88deg auto', 'environment-image':environmentSource(stage.dataset.modelEnvironment), exposure:stage.dataset.modelExposure||'1', 'shadow-intensity':stage.dataset.modelShadow||'.6', 'shadow-softness':'1', reveal:'auto'};
        Object.entries(attrs).forEach(([name, value]) => viewer.setAttribute(name, value));
        on(viewer, 'load', () => {
          if (signal.aborted || id !== pass) return;
          clearTimeout(timeout); loading = false; clearStatus(); poster.hidden = true;
          stage.classList.add('model-loaded'); stage.dataset.modelState = 'loaded'; syncMotion();
        }, {once:true});
        on(viewer, 'error', event => {console.warn('Moviwi model load failure.',event.detail);fail(id,event.detail?.type || 'loadfailure');}, {once:true});
        on(viewer, 'progress', event => {
          if (!loading || signal.aborted || id !== pass) return;
          const next = Number(event.detail?.totalProgress) || 0;
          if (next > progress) {progress = next;armTimeout(id);}
        });
        // Only an actual camera interaction takes control; a vertical touch scroll does not.
        on(viewer, 'camera-change', event => {
          if (event.detail?.source !== 'user-interaction') return;
          interacting = true; stage.dataset.modelControl = 'manual';
        });
        viewer.setAttribute('src', assetSource(stage.dataset.modelUrl)); target.hidden = false; target.replaceChildren(viewer);
      } catch (error) {
        if (signal.aborted || id !== pass) return;
        fail(id,error?.message || 'library'); console.warn('Moviwi 3D view could not load.', error);
      }
    }
    const loadVisible = () => {if (visible && !signal.aborted) load();};
    const observer = new IntersectionObserver(entries => {visible = entries[0].isIntersecting; if (visible) {if(document.readyState === 'complete') loadVisible(); schedule();}}, {rootMargin:'100px 0px'});
    on(window, 'load', loadVisible, {once:true});
    observer.observe(story);
    on(window, 'scroll', schedule, {passive:true}); on(window, 'resize', schedule, {passive:true});
    on(reduced, 'change', syncMotion); on(mobile, 'change', schedule); on(document, 'visibilitychange', schedule);
    on(document, 'shopify:block:select', event => {
      const index = features.findIndex(el => el.contains(event.target));
      if (index < 0 || !motion()) return;
      const box = story.getBoundingClientRect(), p = .08 + (index+.5)/features.length*.84;
      window.scrollTo({top:scrollY + box.top + (box.height-pin.getBoundingClientRect().height)*p, behavior:'instant'}); schedule();
    });
    select(0); syncMotion();
    controllers.set(root, () => {
      abort.abort(); observer.disconnect(); cancelAnimationFrame(frame); clearTimeout(retry); showImage();
      story.classList.remove('product-story-ready'); story.style.removeProperty('--story-progress'); stage.removeAttribute('style');
      features.forEach(el => el.classList.remove('is-current')); controllers.delete(root);
    });
  }
  const scan = () => document.querySelectorAll('.shopify-section').forEach(mount);
  window.MoviwiHomeModel = {scan};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, {once:true}); else scan();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => controllers.get(event.target)?.());
})();
